import { createContext, useContext, useReducer } from 'react'
import type { TogethernessMode } from '../lib/types'

/**
 * Shared wizard state (design-system §6.0): the session context the thin client
 * gathers — roster + togetherness + vibe. The coverage-k control is retired:
 * the client always sends coverage_k = roster size (§6.3). NO scoring/ranking
 * lives here; this only collects inputs for POST /sessions.
 */
export interface SessionBuilderState {
  groupId: string | null
  rosterUserIds: string[]
  togethernessMode: TogethernessMode
  togethernessRequired: boolean
  vibeChips: string[]
  vibeText: string
  wildcardOptIn: boolean
}

type Action =
  | { type: 'setGroup'; groupId: string | null }
  | { type: 'toggleMember'; userId: string }
  | { type: 'setRoster'; userIds: string[] }
  | { type: 'setTogethernessMode'; mode: TogethernessMode }
  | { type: 'setTogethernessRequired'; required: boolean }
  | { type: 'toggleVibeChip'; chip: string }
  | { type: 'setVibeText'; text: string }
  | { type: 'setWildcardOptIn'; optIn: boolean }
  | { type: 'reset'; state?: Partial<SessionBuilderState> }

export const initialBuilderState: SessionBuilderState = {
  groupId: null,
  rosterUserIds: [],
  togethernessMode: 'true_coop',
  togethernessRequired: false,
  vibeChips: [],
  vibeText: '',
  wildcardOptIn: false,
}

function reducer(state: SessionBuilderState, action: Action): SessionBuilderState {
  switch (action.type) {
    case 'setGroup':
      return { ...state, groupId: action.groupId }
    case 'toggleMember': {
      const has = state.rosterUserIds.includes(action.userId)
      const roster = has
        ? state.rosterUserIds.filter((id) => id !== action.userId)
        : [...state.rosterUserIds, action.userId]
      return { ...state, rosterUserIds: roster }
    }
    case 'setRoster':
      return { ...state, rosterUserIds: action.userIds }
    case 'setTogethernessMode':
      return { ...state, togethernessMode: action.mode }
    case 'setTogethernessRequired':
      return { ...state, togethernessRequired: action.required }
    case 'toggleVibeChip': {
      const has = state.vibeChips.includes(action.chip)
      return {
        ...state,
        vibeChips: has ? state.vibeChips.filter((c) => c !== action.chip) : [...state.vibeChips, action.chip],
      }
    }
    case 'setVibeText':
      return { ...state, vibeText: action.text }
    case 'setWildcardOptIn':
      return { ...state, wildcardOptIn: action.optIn }
    case 'reset':
      return { ...initialBuilderState, ...action.state }
    default:
      return state
  }
}

interface SessionBuilderContextValue {
  state: SessionBuilderState
  dispatch: React.Dispatch<Action>
}

const SessionBuilderContext = createContext<SessionBuilderContextValue | null>(null)

export function SessionBuilderProvider({
  children,
  initial,
}: {
  children: React.ReactNode
  initial?: Partial<SessionBuilderState>
}) {
  const [state, dispatch] = useReducer(reducer, { ...initialBuilderState, ...initial })
  return (
    <SessionBuilderContext.Provider value={{ state, dispatch }}>{children}</SessionBuilderContext.Provider>
  )
}

export function useSessionBuilder() {
  const ctx = useContext(SessionBuilderContext)
  if (!ctx) throw new Error('useSessionBuilder must be used within a SessionBuilderProvider')
  return ctx
}
